import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, UserPlus, Phone, ChevronRight } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";

const EMPTY = {
  name: "", phone: "", address: "", id_type: "Aadhaar", id_number: "", notes: "",
  photo: "", id_proof: "",
  guarantor_name: "", guarantor_phone: "", guarantor_address: "", guarantor_relation: "", guarantor_photo: "",
};

export default function Customers() {
  const { currency } = useSettings();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async (q = "") => {
    const r = await api.get("/customers", { params: { search: q } });
    setCustomers([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false); setForm(EMPTY);
      navigate(`/customers/${r.data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Customers</h1>
          <p className="text-slate-500 mt-1">Manage borrowers and their loans</p>
        </div>
        <Button className="bg-maroon hover:bg-maroon-hover" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-new-customer-button">
          <UserPlus className="h-4 w-4 mr-2" /> Add Customer
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input className="pl-9" placeholder="Search by name or phone…" value={search}
          onChange={(e) => { setSearch(e.target.value); load(e.target.value); }} data-testid="customer-search-input" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {customers.length ? customers.map((c) => (
          <div key={c.id} onClick={() => navigate(`/customers/${c.id}`)} data-testid={`customer-card-${c.id}`}
            className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-maroon/40 transition-all cursor-pointer group">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-navy text-white flex items-center justify-center font-heading font-bold">
                  {c.name[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-navy">{c.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone || "—"}</div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-maroon transition-colors" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-slate-400">Active Loans</div>
                <div className="font-semibold text-navy">{c.active_loans} / {c.loan_count}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Outstanding</div>
                <div className="font-mono font-semibold text-maroon">{fmtMoney(c.total_outstanding, currency)}</div>
              </div>
            </div>
          </div>
        )) : (
          <div className="col-span-full bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
            <UserPlus className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">No customers found. Add your first customer.</p>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-customer-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Full Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" /></div>
            <div><Label>Phone Number</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" /></div>
            <div><Label>ID Type</Label>
              <Input value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} placeholder="Aadhaar / PAN / Voter ID" data-testid="customer-idtype-input" /></div>
            <div className="sm:col-span-2"><Label>ID / Document Number</Label>
              <Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} data-testid="customer-idnumber-input" /></div>
            <div className="sm:col-span-2"><Label>Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="customer-address-input" /></div>

            <div><ImageUpload label="Customer Photo (optional)" value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} testid="customer-photo-upload" /></div>
            <div><ImageUpload label="ID Proof Photo" value={form.id_proof} onChange={(v) => setForm({ ...form, id_proof: v })} testid="customer-idproof-upload" /></div>

            <div className="sm:col-span-2 pt-2 border-t border-slate-100">
              <p className="text-sm font-semibold text-navy font-heading">Guarantor Details</p>
            </div>
            <div><Label>Guarantor Name</Label>
              <Input value={form.guarantor_name} onChange={(e) => setForm({ ...form, guarantor_name: e.target.value })} data-testid="guarantor-name-input" /></div>
            <div><Label>Guarantor Phone</Label>
              <Input value={form.guarantor_phone} onChange={(e) => setForm({ ...form, guarantor_phone: e.target.value })} data-testid="guarantor-phone-input" /></div>
            <div><Label>Relation to Customer</Label>
              <Input value={form.guarantor_relation} onChange={(e) => setForm({ ...form, guarantor_relation: e.target.value })} placeholder="Brother / Friend / Colleague" data-testid="guarantor-relation-input" /></div>
            <div><Label>Guarantor Address</Label>
              <Input value={form.guarantor_address} onChange={(e) => setForm({ ...form, guarantor_address: e.target.value })} data-testid="guarantor-address-input" /></div>
            <div className="sm:col-span-2">
              <ImageUpload
                label="Guarantor Photo (optional)"
                value={form.guarantor_photo}
                onChange={(v) => setForm({ ...form, guarantor_photo: v })}
                testid="guarantor-photo-upload"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-maroon hover:bg-maroon-hover" onClick={create} disabled={saving} data-testid="save-customer-button">
              {saving ? "Saving…" : "Save Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
